import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../api/client";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function OrderConfirmation() {
    const [sp] = useSearchParams();
    const sessionId = sp.get("session_id");
    const [state, setState] = useState("polling"); // polling, paid, expired, failed
    const [order, setOrder] = useState(null);
    const attemptsRef = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState("failed");
            return;
        }
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            if (attemptsRef.current >= 10) {
                setState("expired");
                return;
            }
            attemptsRef.current += 1;
            try {
                const { data } = await api.get(`/checkout/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setOrder(data.order);
                    setState("paid");
                    return;
                }
                if (data.status === "expired") {
                    setState("expired");
                    return;
                }
                setTimeout(poll, 2000);
            } catch {
                setTimeout(poll, 2500);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 animate-fade-slide">
            {state === "polling" && (
                <div className="bg-white border border-brand-line rounded-3xl p-10 text-center">
                    <Loader2 size={48} className="mx-auto animate-spin text-brand-orange mb-4" />
                    <h1 className="font-heading font-black text-3xl mb-2">Confirming your payment...</h1>
                    <p className="text-brand-moss">Hold tight, we're just double-checking with Stripe.</p>
                </div>
            )}
            {state === "paid" && (
                <div data-testid="order-success" className="bg-white border border-brand-line rounded-3xl p-10 text-center">
                    <div className="w-20 h-20 rounded-full bg-brand-sage/10 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 size={48} className="text-brand-sage" />
                    </div>
                    <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange mb-2">Order confirmed</div>
                    <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter">
                        Yay! Your crunch is on the way.
                    </h1>
                    {order && (
                        <>
                            <p className="text-brand-moss mt-4">
                                Order ID: <span data-testid="order-id" className="font-bold text-brand-ink">{order.id}</span>
                            </p>
                            <div className="bg-brand-cream rounded-2xl p-6 mt-8 text-left">
                                <h3 className="font-heading font-bold text-lg mb-4">Items</h3>
                                <div className="space-y-3">
                                    {order.items.map((i, idx) => (
                                        <div key={idx} className="flex justify-between text-sm">
                                            <span>{i.name} × {i.quantity}</span>
                                            <span className="font-bold">₹{(i.price * i.quantity).toFixed(0)}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="border-t border-brand-line mt-4 pt-4 flex justify-between">
                                    <span className="font-bold">Total paid</span>
                                    <span className="font-heading font-black text-xl">₹{order.totals.total.toFixed(2)}</span>
                                </div>
                            </div>
                        </>
                    )}
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/track" data-testid="track-order-link" className="bg-brand-ink text-white font-bold px-6 py-3 rounded-xl hover:bg-brand-orange transition">
                            Track my order
                        </Link>
                        <Link to="/products" className="border-2 border-brand-ink text-brand-ink font-bold px-6 py-3 rounded-xl hover:bg-brand-ink hover:text-white transition">
                            Keep shopping
                        </Link>
                    </div>
                </div>
            )}
            {(state === "expired" || state === "failed") && (
                <div className="bg-white border border-brand-line rounded-3xl p-10 text-center">
                    <XCircle size={48} className="mx-auto text-destructive mb-4" />
                    <h1 className="font-heading font-black text-3xl mb-2">Payment not completed</h1>
                    <p className="text-brand-moss mb-6">Your session expired or payment was cancelled.</p>
                    <Link to="/cart" className="bg-brand-orange text-white font-bold px-6 py-3 rounded-xl">
                        Try again
                    </Link>
                </div>
            )}
        </div>
    );
}
